import { useState, useRef, useEffect } from 'react';
import { format, isToday, isYesterday, isSameDay } from 'date-fns';
import { getSocket } from '../../services/socket';

// Date separator component
const DateSeparator = ({ date }) => {
  let label;
  if (isToday(date)) {
    label = 'Today';
  } else if (isYesterday(date)) {
    label = 'Yesterday';
  } else {
    label = format(date, 'MMMM d, yyyy');
  }

  return (
    <div className="flex items-center justify-center my-4">
      <div className="px-4 py-1.5 bg-jp-section rounded-full">
        <span className="text-xs text-jp-muted font-medium">{label}</span>
      </div>
    </div>
  );
};

export const ChatArea = ({
  selectedChat,
  messages = [],
  loading,
  onBack,
  onToggleProfile,
  onSendMessage,
  onSendFirstMessage,
  currentUserId,
  sendingMessage,
  startingConversation,
  onlineUsers = [],
  typingUsers = {}
}) => {
  const [message, setMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);

  const isNewConversation = selectedChat?.isNew === true;

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if ((!message.trim() && selectedFiles.length === 0) || sendingMessage || startingConversation) return;

    if (isNewConversation && onSendFirstMessage) {
      // Start new conversation with first message
      onSendFirstMessage(selectedChat.participant._id, message, selectedFiles);
    } else {
      onSendMessage(message, selectedFiles);
    }
    setMessage('');
    setSelectedFiles([]);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setSelectedFiles((prev) => [...prev, ...files]);
    }
    // Reset file input value so the same file can be selected again
    e.target.value = '';
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const isImage = (file) => {
    if (typeof file === 'string') {
      // URL string - check extension
      return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(file);
    }
    // File object from backend API (has url and name)
    if (file.name && file.url) {
      return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(file.name);
    }
    // Browser File object (from file input)
    return file.type?.startsWith('image/');
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFilePreview = (file) => {
    if (typeof file === 'string') return file;
    return URL.createObjectURL(file);
  };

  const getFileName = (file) => {
    if (typeof file === 'string') {
      return file.split('/').pop();
    }
    return file.name;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const participant = selectedChat?.participant || {};
  const participantId = participant._id?.toString();
  const isParticipantOnline = participantId && onlineUsers.some(id => id.toString() === participantId);
  const isParticipantTyping = selectedChat?._id && typingUsers[selectedChat._id];

  // Emit typing events
  const handleTypingEmit = () => {
    if (!selectedChat?._id) return;
    const socket = getSocket();
    if (!socket) return;

    socket.emit('typing', { chatId: selectedChat._id });

    // Clear previous timeout
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    // Stop typing after 2 seconds of no input
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit('stopTyping', { chatId: selectedChat._id });
    }, 2000);
  };

  if (!selectedChat) {
    return (
      <div className="flex-1 flex flex-col bg-jp-card">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 bg-jp-section rounded-full flex items-center justify-center">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-jp-muted" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-jp-ink font-semibold text-lg mb-2">No message selected</h3>
            <p className="text-jp-muted text-sm">Select a conversation to start messaging</p>
          </div>
        </div>
      </div>
    );
  }

  const displayName = `${participant.firstName || ''} ${participant.lastName || ''}`.trim() || 'Unknown';
  const initials = displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="flex-1 flex flex-col bg-jp-card">
      {/* Chat Header */}
      <div className="flex items-center justify-between px-3 sm:px-4 md:px-6 py-3 sm:py-4 border-b border-jp-line bg-jp-card">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          {onBack && (
            <button
              onClick={onBack}
              className="md:hidden p-2 hover:bg-jp-line rounded-lg transition-colors text-jp-ink flex-shrink-0"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <div className="relative flex-shrink-0">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-sm sm:text-base overflow-hidden">
              {participant.profileImage ? (
                <img src={participant.profileImage} alt={displayName} className="w-full h-full object-cover" />
              ) : (
                initials
              )}
            </div>
            {isParticipantOnline && (
              <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-jp-card rounded-full" />
            )}
          </div>
          <div className="min-w-0">
            <h3 className="text-jp-ink font-semibold text-sm sm:text-base truncate">{displayName}</h3>
            <p className="text-xs truncate">
              {isParticipantTyping ? (
                <span className="text-green-400">typing...</span>
              ) : isParticipantOnline ? (
                <span className="text-green-400">Online</span>
              ) : (
                <span className="text-jp-muted">Offline</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-jp-muted text-xs sm:text-sm flex-shrink-0 hidden sm:block">
            {selectedChat.updatedAt ? format(new Date(selectedChat.updatedAt), 'dd MMM yyyy') : ''}
          </div>
          {onToggleProfile && (
            <button
              onClick={onToggleProfile}
              className="lg:hidden p-2 hover:bg-jp-line rounded-lg transition-colors text-jp-ink flex-shrink-0"
              title="View Profile"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : messages.length > 0 ? (
          messages.map((msg, index) => {
            const isSent = (msg.sender?._id || msg.senderId) === currentUserId;
            const time = msg.createdAt ? format(new Date(msg.createdAt), 'h:mm a') : '';
            const msgDate = msg.createdAt ? new Date(msg.createdAt) : null;

            // Check if we need to show date separator
            const prevMsg = index > 0 ? messages[index - 1] : null;
            const prevMsgDate = prevMsg?.createdAt ? new Date(prevMsg.createdAt) : null;
            const showDateSeparator = msgDate && (!prevMsgDate || !isSameDay(msgDate, prevMsgDate));

            return (
              <div key={msg._id}>
                {/* Date Separator */}
                {showDateSeparator && <DateSeparator date={msgDate} />}

                {/* Message */}
                <div className={`flex ${isSent ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] sm:max-w-md px-3 sm:px-4 py-2 sm:py-2.5 rounded-2xl ${
                    isSent
                      ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white'
                      : 'bg-blue-600 text-white'
                  }`}>
                    {/* File Attachment */}
                    {msg.file && (
                      <div className="mb-2">
                        {isImage(msg.file) ? (
                          <a href={msg.file.url} target="_blank" rel="noopener noreferrer">
                            <img
                              src={msg.file.url}
                              alt={msg.file.name || 'attachment'}
                              className="max-w-full rounded-lg max-h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                            />
                          </a>
                        ) : (
                          <div className="flex items-center gap-3 bg-white/10 rounded-lg px-3 py-2.5">
                            <div className="flex-shrink-0 w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium truncate">{msg.file.name}</p>
                              {msg.file.size && (
                                <p className="text-[10px] opacity-70">{formatFileSize(msg.file.size)}</p>
                              )}
                            </div>
                            <a
                              href={msg.file.url}
                              download={msg.file.name}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-shrink-0 p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                              title="Download"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                    {msg.text && <p className="text-xs sm:text-sm break-words">{msg.text}</p>}
                    <div className="flex items-center justify-end gap-1 mt-1">
                      <span className="text-xs opacity-75">{time}</span>
                      {isSent && (
                        <span className="text-xs opacity-75">
                          {msg.isRead ? '✓✓' : '✓'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              {isNewConversation ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 bg-cyan-500/20 rounded-full flex items-center justify-center">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-jp-accent-ink" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <p className="text-jp-accent-ink font-medium">Start a new conversation</p>
                  <p className="text-jp-muted text-sm mt-1">Send your first message to {selectedChat?.participant?.firstName}</p>
                </>
              ) : (
                <p className="text-jp-muted text-sm">No messages yet. Say hello!</p>
              )}
            </div>
          </div>
        )}
        {isParticipantTyping && (
          <div className="flex justify-start">
            <div className="bg-jp-section px-4 py-2.5 rounded-2xl">
              <div className="flex gap-1 items-center">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="p-2 sm:p-3 md:p-4 border-t border-jp-line bg-jp-card">
        {/* File Preview */}
        {selectedFiles.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {selectedFiles.map((file, index) => (
              <div key={index} className="relative group">
                {isImage(file) ? (
                  <div className="relative">
                    <img
                      src={getFilePreview(file)}
                      alt={getFileName(file)}
                      className="h-16 w-16 object-cover rounded-lg border border-jp-line"
                    />
                    <button
                      onClick={() => removeFile(index)}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors"
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="relative flex items-center gap-2 bg-jp-section rounded-lg px-3 py-2 pr-8">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-jp-muted">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <span className="text-xs text-jp-muted truncate max-w-[100px]">{getFileName(file)}</span>
                    <button
                      onClick={() => removeFile(index)}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors"
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-1 sm:gap-2 md:gap-3">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.gif,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Attachment button */}
          <button
            onClick={openFilePicker}
            disabled={sendingMessage || startingConversation}
            className="p-2 sm:p-2.5 hover:bg-jp-line rounded-full transition-colors text-jp-muted hover:text-jp-ink flex-shrink-0 disabled:opacity-50"
            title="Attach file"
          >
            <svg width="18" height="18" className="sm:w-[22px] sm:h-[22px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
          </button>

          <input
            type="text"
            value={message}
            onChange={(e) => { setMessage(e.target.value); if (!isNewConversation) handleTypingEmit(); }}
            onKeyDown={handleKeyDown}
            placeholder={isNewConversation ? "Type your first message..." : "Send a message"}
            className="flex-1 px-3 sm:px-4 py-2 sm:py-3 bg-jp-section border border-jp-line rounded-full text-jp-ink placeholder-jp-muted/70 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/50"
            disabled={sendingMessage || startingConversation}
          />

          <button
            onClick={handleSend}
            disabled={(!message.trim() && selectedFiles.length === 0) || sendingMessage || startingConversation}
            className="p-2 sm:p-2.5 hover:bg-jp-line rounded-full transition-colors text-jp-muted hover:text-jp-ink flex-shrink-0 disabled:opacity-50"
          >
            <svg width="18" height="18" className="sm:w-[22px] sm:h-[22px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};
